import { useTranslation } from 'react-i18next'
import { Pencil } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type OrganizationContactSectionCardProps = {
  contactName: string
  contactEmail: string
  contactPhoneNumber: string
  showCompleteToSaveHint?: boolean
  showRequiredIndicators?: boolean
  variant?: 'card' | 'plain'
  onContactChange: (evt: React.ChangeEvent<HTMLInputElement>) => void
}

export function OrganizationContactSectionCard({
  contactName,
  contactEmail,
  contactPhoneNumber,
  showCompleteToSaveHint = false,
  showRequiredIndicators = true,
  variant = 'card',
  onContactChange,
}: OrganizationContactSectionCardProps) {
  const { t } = useTranslation(['settings', 'common'])
  const requiredMark = showRequiredIndicators ? (
    <span className="text-destructive"> *</span>
  ) : null

  const fields = (
    <>
      {showCompleteToSaveHint && (
        <Alert>
          <AlertDescription>
            {t('settings:organizationDetails.contact.completeToSaveHint')}
          </AlertDescription>
        </Alert>
      )}
      <div className="grid gap-2">
        <Label htmlFor="contactName">
          {t('settings:organizationDetails.contact.fields.contactName')}
          {requiredMark}
        </Label>
        <Input
          id="contactName"
          value={contactName}
          onChange={onContactChange}
          aria-required={showRequiredIndicators}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="contactEmail">
          {t('common:inputFields.email.label')}
          {requiredMark}
        </Label>
        <Input
          id="contactEmail"
          type="email"
          value={contactEmail}
          onChange={onContactChange}
          aria-required={showRequiredIndicators}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="contactPhoneNumber">
          {t('common:inputFields.phoneNumber.label')}
          {requiredMark}
        </Label>
        <Input
          id="contactPhoneNumber"
          value={contactPhoneNumber}
          onChange={onContactChange}
          aria-required={showRequiredIndicators}
        />
      </div>
    </>
  )

  if (variant === 'plain') {
    return <div className="space-y-4">{fields}</div>
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Pencil className="size-5 shrink-0" />
          <CardTitle>{t('settings:organizationDetails.contact.title')}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">{fields}</CardContent>
    </Card>
  )
}
